import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { formatMoneyWithCurrency, formatRut } from 'src/utils/formatter';
import moment from 'moment';
import { getDescription, getName } from './components/matchRowHelpers';
import { DocumentType } from 'src/utils/enum';
import { openDocumentDetail } from 'src/utils/documentModal';
import { dt, getDocumentTypeDisplayLabel } from 'src/utils/documentsI18n';

const DocumentInfoBar = ({ editData }) => {
  const history = useHistory();
  const location = useLocation();

  if (!editData) {
    return null;
  }

  const amount = editData.meta?.amount || editData.amount || 0;
  const currency = editData.meta?.currency || editData.currencyType || 'CLP';
  const isReceived = editData.meta?.isReceived === true || editData.meta?.isReceived === false ? editData.meta.isReceived : true;
  
  // Calculate remaining amount - check both direct fields and meta fields
  const remaining = editData.meta?.remaining !== undefined 
    ? editData.meta?.remaining 
    : (editData.remaining !== undefined ? editData.remaining : amount);
  
  // Get document type label
  const documentType = getDocumentTypeDisplayLabel(editData) || dt('common.document');
  
  // Get document ID/number
  const documentId = editData.meta?.documentId || editData.meta?.number || '';
  
  // Get date
  const documentDate = editData.documentDate || editData.meta?.documentDate || editData.meta?.date;
  
  // Build description using the same pattern as MatchItem
  // Use getName helper which already formats description + issuer/receiver info correctly
  // But we need to ensure editData has documentType for getName to work
  const documentTypeForHelper = editData.documentType || editData.meta?.documentType;
  const matchData = { ...editData, documentType: documentTypeForHelper };
  const fullDescription = getName(matchData);
  
  // Check if document is clickable (same logic as MatchItem)
  const isClickable = editData.id && [DocumentType.TAX_DOCUMENT, DocumentType.OBLIGATION, DocumentType.CREDIT].includes(documentTypeForHelper);
  
  // Handler to open document detail modal
  const handleDocumentClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isClickable) {
      openDocumentDetail(editData.id || editData._id, history, location);
    }
  };

  return (
    <>
      <div className="tw-w-full tw-bg-gray-50 tw-rounded-lg tw-p-6 tw-mb-4 tw-shadow-sm">
        <div className="tw-flex tw-items-start tw-gap-4">
          {/* Main Content */}
          <div className="tw-flex-1 tw-min-w-0">
            {/* Amount and Date Row */}
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-mb-2">
              <div className="tw-flex tw-items-center tw-gap-3">
                {/* Document Type before amount - bigger and bolder, clickable */}
                <span 
                  className={`tw-font-bold tw-underline ${isClickable ? 'tw-cursor-pointer hover:tw-underline' : ''}`}
                  style={{ 
                    fontSize: '1.3rem',
                    color: isClickable ? '#176f80' : '#1f2d3a'
                  }}
                  onClick={isClickable ? handleDocumentClick : undefined}
                >
                  {documentType}{documentId ? ` #${documentId}` : ''}
                </span>
                <span className={`tw-text-lg tw-font-semibold ${isReceived ? 'tw-text-red-600' : 'tw-text-green-600'}`}>
                  {isReceived ? '↓' : '↑'} {formatMoneyWithCurrency(amount, currency)}
                  {editData.meta?.exchange?.originalCurrency && editData.meta.exchange.originalCurrency !== 'CLP' && editData.meta?.exchange?.originalAmount && (
                    <span className="tw-text-sm tw-font-normal tw-text-gray-600">
                      {' '}({formatMoneyWithCurrency(Math.abs(editData.meta.exchange.originalAmount), editData.meta.exchange.originalCurrency)})
                    </span>
                  )}
                </span>
                {documentDate && (
                  <span className="tw-text-xs tw-text-gray-500 tw-align-middle">
                    {dt('reconcile.issuedOn')} {moment(documentDate).format('DD/MM/YY')}
                  </span>
                )}
              </div>
              
              {/* Remaining Amount Badge */}
              <div className="tw-flex-shrink-0">
                <span className={`tw-inline-flex tw-items-center tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-semibold tw-whitespace-nowrap ${
                  remaining === 0 
                    ? 'tw-bg-green-100 tw-text-green-700' 
                    : 'tw-bg-blue-100 tw-text-blue-700'
                }`}>
                  {remaining === 0
                    ? `✓ ${dt('reconcile.reconciled')}`
                    : dt('reconcile.remaining', {
                        amount: formatMoneyWithCurrency(remaining, currency),
                      })}
                </span>
              </div>
            </div>

            {/* Description */}
            {fullDescription && (
              <div className="tw-text-sm tw-text-gray-700 tw-leading-relaxed">
                {fullDescription}
              </div>
            )}
            
            {/* Exchange Rate Info - same as MatchItem */}
            {editData.meta?.exchange?.exchangeRate && editData.meta.exchange.exchangeRate > 1 && editData.meta?.exchange?.originalAmount && (
              <div style={{ 
                fontSize: '11px', 
                color: '#6B7280',
                marginTop: '4px'
              }}>
                {dt('reconcile.mustPayAtRate', {
                  amount: formatMoneyWithCurrency(
                    Math.abs(editData.meta.exchange.originalAmount),
                    editData.meta.exchange.originalCurrency || currency || 'CLP'
                  ),
                  rate: editData.meta.exchange.exchangeRate.toFixed(2),
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DocumentInfoBar;

